// Wire-format types for the Patient Management feature (PRD 01).
// Contract: BE returns raw (unmasked) values; FE owns all masking per PRD §4.3.

// ── Enums ────────────────────────────────────────────────────────────

export type PatientGender = 'M' | 'F';

// PRD §4.4 — required when status === 'CLOSED'.
export type PatientCloseReason =
  | 'COMPLETED'
  | 'WITHDRAWN'
  | 'REHOSPITALIZED'
  | 'OTHER';

export const PATIENT_CLOSE_REASONS: readonly PatientCloseReason[] = [
  'COMPLETED',
  'WITHDRAWN',
  'REHOSPITALIZED',
  'OTHER',
] as const;

// PRD §4.4 + §3.1 — status is the canonical column; `ending` is a UI-only
// filter derived from (active && days-until-expiry <= 30).
export type PatientStatus = 'ACTIVE' | 'CLOSED' | 'WITHDRAWN';
export type PatientStatusFilter = 'all' | 'ACTIVE' | 'ending' | 'CLOSED' | 'WITHDRAWN';

// PRD §3.7 — sign-up actor for the registration modal.
export type PatientAppUserType = 'patient' | 'guardian';

export type PatientGuardianRelationship =
  | 'SPOUSE'
  | 'CHILD'
  | 'PARENT'
  | 'SIBLING'
  | 'OTHER';

// PRD §4.1 — sortable columns (Phone + Status are non-sortable).
export type PatientSortKey =
  | 'id'
  | 'name'
  | 'gender'
  | 'birthDate'
  | 'dischargeDate'
  | 'registeredAt';

export type SortDirection = 'asc' | 'desc';

// ── Entities ─────────────────────────────────────────────────────────

/**
 * One 처방약 row — 약품명 paired with its 복약주기 (SHCT-738 · SHCT-737).
 *
 * The pairing is the point: the registration form takes a LIST, and flattening it into one drug string
 * plus one cycle string loses which cycle belonged to which drug the moment there is more than one. That
 * is exactly what `pt_patient.prescription_drug` did and why V96 replaced it with a child table.
 *
 * `freq` is optional data, not an optional field: PRD 02 §3.7 and 의료진웹 03 §3.2 both make 복약주기 a
 * 선택 입력, so a drug saved without one is a legitimate state and comes back as an empty string.
 */
export interface PatientMedication {
  name: string;
  freq: string;
}

/**
 * One 담당 의료진 assignment (SHCT-910 B-1 · SHCT-981). The server lists them 주담당 first, then by
 * assignment time and id — there is no stored order column, and the FIRST entry is the 주담당.
 * `name` is null when the staff account was deleted but the assignment row is still there: the row is
 * kept on the wire so an edit round-trip cannot silently drop it.
 */
export interface PatientAssignedStaff {
  id: number;
  name: string | null;
  primary: boolean;
}

// PRD §3.6 — 15 read-only display fields + memo (the only editable one).
// All FE masking happens in utils/format-patient — the wire shape is raw.
export interface Patient {
  // v9 (DEC-DB-PK-BIGINT-001): ad_patient.id is BIGINT UNSIGNED → number on the wire.
  id: number;
  // Public-facing identifier, format "P-{id}" (no padding). PRD §4.3.
  pid: string;
  name: string;
  gender: PatientGender | null;
  // YYYY-MM-DD.
  birthDate: string;
  // 010-XXXX-YYYY. NULL for a guardian-mode patient who has no phone of their own — PRD 02 §3.7 makes
  // 환자 휴대폰 optional in 보호자 대리 mode, and admin-api stores null rather than "" for it. Typed
  // honestly so a caller cannot hand it to a `string`-only formatter and get a blank cell. (SHCT-794)
  phone: string | null;
  email: string | null;
  // cm. PRD §4.3 stores 100..220.
  height: number | null;
  // kg, 1 decimal. PRD §4.3 stores 20.0..300.0.
  weight: number | null;
  // 거주지 — the address the patient picked from the national road-name address service, EXACTLY as
  // stored (SHCT-915). It used to arrive shortened to 시 / 구 / 동, which is why the panel showed a
  // different string from the one the patient had entered in the app; admin-api stopped shortening it
  // when the address moved onto the patient's own row. Null when the patient has no address recorded.
  residence: string | null;
  // 상세 주소 — the half the patient types themselves (a floor, a unit, a building name). Optional and
  // independently null: a road-name address with no unit number is an ordinary record, not a broken one,
  // so this is null while `residence` is present. PRD 02 v1.23 §3.6 gives it its own panel row.
  residenceDetail: string | null;
  // SHCT-738 [ADM.064] — the record holds TWO diagnosis references, not three. 주진단 points at a disease
  // GROUP (a family of illnesses, so a name and no ICD code); 부진단 points at a disease CODE (one concrete
  // illness, hence code + name). PRD 02 v1.20 §3.6 deleted the separate 질병군 row it used to list, because
  // 주진단 IS the 질병군 — the two were the same value written twice.
  mainDiagnosisName?: string | null;
  // 부진단. ICD code + display name (split so the FE can render either independently). The names predate
  // v1.20 and are kept because the wire uses them; only the meaning is now stated rather than implied.
  diseaseCode: string | null;
  diseaseName: string | null;
  // NOTE: the wire also carries `diseaseGroup`, the group owning the SECONDARY code. It is deliberately NOT
  // in this type: v1.20 removed the 질병군 row, and rendering that value under a 주진단-shaped label would
  // show the wrong level of the diagnosis for any patient whose two references disagree.
  // 처방약 · 복약주기. PRD 02 v1.20 §3.6 defines this as a MULTI-entry list kept in input order.
  //
  // TWO shapes, because the backend is mid-migration. `medications` is the real one: `pt_patient_medication`
  // (V96) holds one row per drug, which is what the 의료진웹 registration form writes and what medical-api
  // already serves. `prescriptionDrug`/`medicationCycle` are the single superseded pair still on `pt_patient`,
  // which V96 documents as "to be dropped in a later migration once no code reads them" — admin-api is that
  // code. Until admin-api reads the table, a patient registered on 의료진웹 with several drugs reaches this
  // panel with the pair EMPTY, which is why 처방약 renders as `-` for them (SHCT-738 QC).
  //
  // The panel prefers `medications` whenever the wire supplies it and falls back to the pair otherwise, so
  // the day admin-api serves the list the panel switches over on its own. `undefined` means "this server
  // does not send the field"; `[]` means "this patient has no prescriptions" — the two are NOT the same and
  // must not be collapsed.
  medications?: PatientMedication[];
  prescriptionDrug?: string | null;
  medicationCycle?: string | null;
  // Fixed to a single value at MVP per PRD §3.6.
  institution: string;
  // Hospital-issued registration number (e.g., "울산-2024-050"). Distinct from `pid`.
  regNo: string;
  // YYYY-MM-DD. Base date for PRO-cycle calculation. PRD §3.6.
  dischargeDate: string | null;
  // 회원가입일 — ISO-8601 datetime. Default-sort key (desc).
  //
  // NULL for a patient who has no app account yet: 회원가입 means the PATIENT APP signup everywhere
  // PRD 02 uses the word (§3.6 거주지 「환자 앱 회원가입 시…」, §9 「환자 앱 회원가입」), and a patient
  // registered by staff has not necessarily signed up. The server still sends the patient row's
  // created_at today; typed nullable ahead of that change so the null does not arrive as a surprise
  // (SHCT-794 QC — see the BE note). Sorting puts nulls last in either direction, matching
  // medical-staff's own registeredAt and this list's 퇴원일.
  registeredAt: string | null;
  // Most-recent PRO submission summary. PRD §3.6.
  lastPro: { itemName: string; submittedAt: string } | null;
  status: PatientStatus;
  // Only set when status === 'CLOSED' (read-only display; admin never edits it — status/closeReason
  // transitions are medical-web's). PRD §4.4.
  closeReason: PatientCloseReason | null;
  // ISO date (YYYY-MM-DD). Only set when status === 'CLOSED'.
  closedAt: string | null;

  // SHCT-794 [ADM.063] — the 앱·계정 section. `PatientDetailResponse` carries none of these yet, so they
  // are OPTIONAL rather than nullable: a field the wire omits is not rendered at all. A dash would be
  // wrong here — §3.6 reads `-` as 미입력, and ADM.063 ② rejected it for the guardian rows precisely
  // because it states "a guardian exists but the data is missing". Absent means "the server does not
  // send this yet"; the BE note covers it.
  appUserType?: PatientAppUserType | null;
  guardianName?: string | null;
  guardianRelationship?: PatientGuardianRelationship | null;
  guardianPhone?: string | null;
  // SHCT-981 — 「3. 담당·연락」 담당 의료진. Optional for the same reason as the fields above: an older
  // server does not send it, and the panel then leaves the row out rather than claiming 「배정 없음」.
  assignedStaff?: PatientAssignedStaff[];
  // NOTE: the medical-staff memo (의료진 메모) was fully removed from the admin patient feature
  // (RULE_MEMO_001, confluence#Q35) — there is no memo field, badge, or edit path.
}

// ── API contract ─────────────────────────────────────────────────────

export interface ListPatientsParams {
  // Free-text — case-insensitive partial match on name / phone / regNo (PRD §3.2).
  q?: string;
  // 'all' is converted to undefined by the caller before the request.
  status?: Exclude<PatientStatusFilter, 'all'>;
  page: number;
  perPage: number;
  sortBy: PatientSortKey;
  sortDir: SortDirection;
}

export interface ListPatientsResult {
  items: Patient[];
  total: number;
  page: number;
  perPage: number;
}

// PRD §3.4 — CSV export bypasses pagination but honors filters + sort.
export interface ExportPatientsParams {
  q?: string;
  status?: Exclude<PatientStatusFilter, 'all'>;
  sortBy: PatientSortKey;
  sortDir: SortDirection;
}

// FEATURE_014 registration payload — the flat shape the backend `PatientCreateRequest` accepts. Guardian
// fields are required only when appUserType === 'GUARDIAN'. Diagnosis is by master id: mainDiagnosisGroupId →
// disease GROUP (required), subDiagnosisCodeId → disease CODE (optional). UID/id are server-assigned.
export interface RegisterPatientInput {
  appUserType: PatientAppUserType;
  name: string;
  birthDate: string;
  gender: PatientGender | null;
  phone: string;
  mrn: string;
  dischargeDate: string;
  height?: number | null;
  weight?: number | null;
  district?: string | null;
  mainDiagnosisGroupId: number;
  subDiagnosisCodeId?: number | null;
  medicationCycle?: string | null;
  prescriptionDrug?: string | null;
  guardianName?: string;
  guardianRelationship?: PatientGuardianRelationship;
  guardianPhone?: string;
  assignedStaffIds: number[];
}

// FEATURE_015 correction payload (PRD 02 v1.37 §3.6 「편집 가능 필드」) — the PUT body of the panel's
// full edit mode. PARTIAL on purpose: [저장] sends only the fields that changed, and the backend reads an
// absent key as "not changing", so one save never replays a stale value over someone else's correction.
//
// SHCT-981 widened it: 등록번호(`mrn`), 환자 휴대폰 in either mode, 보호자 휴대폰, 성별·생년월일 in 본인
// mode, and 담당 의료진. What stays out is the 시스템 생성값 6종 (UID · 회원가입일 · 마지막 PRO · 관리 상태 ·
// 앱 사용자 유형 · 치료기관) — the server ignores them even if sent.
export interface UpdatePatientInput {
  name?: string;
  gender?: PatientGender;
  birthDate?: string;
  /**
   * 환자 휴대폰, hyphenless 11 digits. Editable in both modes since SHCT-981 — app login finds the account
   * by NICE DI, not by number, so changing it moves no account (§3.6 「휴대폰 번호 변경과 계정 연결」).
   * An empty string clears it, which only 보호자 mode allows (there it is a 선택 연락처).
   */
  phone?: string;
  /** 보호자 휴대폰 — 보호자 mode only (SHCT-981). Same format and duplicate rules as `phone`. */
  guardianPhone?: string;
  /** 등록번호(MRN) — 1~20 영문·숫자·하이픈, unique among OTHER patients (409 PATIENT_DUPLICATE_MRN). */
  mrn?: string;
  /**
   * 거주지 · 상세 주소 — both editable here since SHCT-915. They were locked while the address lived on a
   * row belonging to the patient's app account, where an admin edit would have written a second copy the
   * panel never showed; the address now sits on the patient's own clinical row, so an edit here writes the
   * one stored answer both the app and the clinician's screens read.
   *
   * Free text rather than an address lookup: this screen has no road-name search control, and a patient
   * who never installed the app would otherwise have no way to get an address recorded at all.
   *
   * Sent independently — the panel edits them as two rows, and an omitted field means "leave it alone"
   * while an empty string clears it (the endpoint's existing convention).
   */
  residence?: string;
  residenceDetail?: string;
  dischargeDate?: string;
  mainDiagnosisGroupId?: number;
  /** 부진단 — the code to set. Removing the existing one is `clearSubDiagnosis`, not a null here. */
  subDiagnosisCodeId?: number;
  /**
   * Remove the 부진단 currently on the record (SHCT-737 QC ②).
   *
   * 부진단 is optional (PRD 02 §3.7), so "this patient no longer has one" has to be expressible — and it
   * cannot ride on `subDiagnosisCodeId`, because an omitted field and a null field reach the server
   * identically, leaving that one field able to say "set it" and "leave it" but not "remove it". Sending
   * this instead is what stops the blank option from reporting a successful save that changed nothing.
   *
   * The server ignores `subDiagnosisCodeId` when this is true: one request cannot both set and clear the
   * same field.
   */
  clearSubDiagnosis?: boolean;
  /**
   * 처방약 — the whole list, replacing whatever is stored, in the order the operator arranged it.
   *
   * Sent INSTEAD OF the single pair below whenever the server proved it reads the list, by sending
   * `medications` on the detail response. Both shapes exist for the same reason they do on `Patient`:
   * admin-api still stores the superseded pair, so a panel that sent only the list today would save
   * nothing at all.
   */
  medications?: PatientMedication[];
  prescriptionDrug?: string;
  medicationCycle?: string;
  guardianName?: string;
  guardianRelationship?: PatientGuardianRelationship;
  /**
   * 담당 의료진 — replaces the whole assignment; the FIRST id is the 주담당, who alone receives the
   * patient's staff alerts (SHCT-981). Sent only when the list actually changed.
   */
  assignedStaffIds?: number[];
}

// FEATURE_014 registration result — the server-assigned identity + masked name for the success toast.
export interface PatientCreateResult {
  id: number;
  uid: string;
  mrn: string;
  maskedName: string;
  status: PatientStatus;
  createdAt: string;
  // [US-PM-07] Did the app-install SMS actually go out? The BE fires it best-effort AFTER the create
  // transaction commits and, today, only logs a gateway failure instead of reporting it on the 201
  // (its own FLAG_DEBT_PATIENT_SMS_FAILURE_SURFACING_01). So this arrives `undefined` from the current
  // server and the FE keeps its old behaviour; the failure popup opens on an explicit `false` only —
  // never on a missing field, which would put a failure popup on every successful registration.
  smsSent?: boolean;
}

// FEATURE_062 async large-file export — the queued-job acknowledgement (202). The server then generates the
// file in the background and emails the requester a download link; the FE only needs to know it was accepted.
export interface ExportJobResult {
  id: number;
  status: string; // QUEUED on enqueue
}

// ── Errors ───────────────────────────────────────────────────────────

// Canonical error catalog from docs/api-contracts/patient-management.md §1.7.
// FE thrown codes (mock) match BE-emitted codes exactly so field-keyed inline
// messages and toast fallbacks behave identically against either backend.
export type PatientApiErrorCode =
  // 400 — FEATURE_014 registration field validation (codes match the backend ErrorCode enum exactly)
  | 'APP_USER_TYPE_INVALID'
  | 'NAME_INVALID'
  | 'BIRTHDATE_REQUIRED'
  | 'GENDER_REQUIRED'
  | 'PHONE_INVALID'
  | 'MRN_REQUIRED'
  | 'PATIENT_MRN_INVALID'
  | 'DISCHARGE_DATE_REQUIRED'
  | 'DISCHARGE_DATE_FUTURE'
  | 'PRIMARY_DIAGNOSIS_REQUIRED'
  | 'GUARDIAN_NAME_REQUIRED'
  | 'GUARDIAN_RELATIONSHIP_REQUIRED'
  | 'GUARDIAN_PHONE_INVALID'
  // 422 — semantic validation (existence / format)
  | 'PATIENT_DOB_FUTURE_INVALID'
  | 'PRIMARY_DIAGNOSIS_INVALID'
  | 'SECONDARY_DIAGNOSIS_INVALID'
  // 부진단 must belong to the chosen 주진단 group (BE PatientRegistrationService domain-dependency check)
  | 'SECONDARY_DIAGNOSIS_GROUP_MISMATCH'
  | 'PATIENT_STAFF_INVALID'
  // [ADM.030] 주담당의 필수 — BE backstop for the client-side required check (SHCT-537)
  | 'PATIENT_PRIMARY_PHYSICIAN_REQUIRED'
  | 'PATIENT_STAFF_DUPLICATE'
  | 'PATIENT_MEDICATION_INVALID'
  // 400 — 거주지 200자 · 상세 주소 100자 초과 (SHCT-915, admin-api PatientRegistrationService). The editor
  // checks both lengths itself, so these arrive only on a paste the client rule somehow let through —
  // they are mapped onto the field that raised them rather than the generic toast.
  | 'RESIDENCE_TOO_LONG'
  | 'RESIDENCE_DETAIL_TOO_LONG'
  // 400 — the update path's own spellings of the name rules (api-spec PUT /admin/patients/{id}).
  | 'PATIENT_NAME_INVALID'
  | 'GUARDIAN_NAME_INVALID'
  // 422 — a guardian field sent for a 본인-mode patient. The panel never renders those fields in that
  // mode, so reaching this means the draft and the record disagree about the mode.
  | 'GUARDIAN_FIELD_NOT_APPLICABLE'
  // 401 / 403 / 404
  | 'UNAUTHORIZED'
  | 'INSUFFICIENT_PERMISSION'
  | 'NOT_FOUND'
  // 409 — duplicates (PATIENT_DUPLICATE_MRN carries meta {uid, maskedName, status, statusLabel})
  | 'PATIENT_DUPLICATE_MRN'
  | 'PATIENT_MOBILE_DUPLICATE'
  | 'GUARDIAN_MOBILE_DUPLICATE'
  | 'PATIENT_SMS_SEND_FAILED'
  // 403 / 413 / 429 — export (enqueue)
  | 'EXPORT_NOT_PERMITTED'
  | 'EXPORT_ROW_LIMIT_EXCEEDED'
  | 'EXPORT_RATE_LIMITED'
  // 404 / 403 / 409 / 410 — async export job status + token-gated download
  | 'EXPORT_JOB_NOT_FOUND'
  | 'EXPORT_LINK_INVALID'
  | 'EXPORT_NOT_READY'
  | 'EXPORT_LINK_EXPIRED'
  // 500 / fallback
  | 'INTERNAL_ERROR'
  | 'UNKNOWN';

export interface PatientApiErrorMeta {
  // Reserved for BE-supplied counters / retry windows. None used today.
  [key: string]: unknown;
}

export class PatientApiError extends Error {
  readonly code: PatientApiErrorCode;
  readonly fields?: Record<string, string>;
  readonly meta?: PatientApiErrorMeta;

  constructor(
    code: PatientApiErrorCode,
    message: string,
    options?: { fields?: Record<string, string>; meta?: PatientApiErrorMeta },
  ) {
    super(message);
    this.name = 'PatientApiError';
    this.code = code;
    this.fields = options?.fields;
    this.meta = options?.meta;
    Object.setPrototypeOf(this, PatientApiError.prototype);
  }
}

export interface PatientApi {
  listPatients(params: ListPatientsParams): Promise<ListPatientsResult>;
  // CSV export is rendered + streamed by the backend (Root-only, raw PHI); the FE downloads the file as a
  // Blob (no client-side CSV building). FEATURE_062.
  exportPatients(params: ExportPatientsParams): Promise<Blob>;
  // Async large-file export (Root-only): queue a background job that emails a download link. Returns the
  // 202 acknowledgement; the file is delivered by email, not downloaded inline. FEATURE_062.
  requestExportJob(params: ExportPatientsParams): Promise<ExportJobResult>;
  getPatient(id: number): Promise<Patient>;
  registerPatient(input: RegisterPatientInput): Promise<PatientCreateResult>;
  // FEATURE_015 — correct an existing patient. `PATIENT:WRITE`, not Root: a typo correction should not
  // queue behind a single account (PRD 02 §3.6).
  updatePatient(id: number, input: UpdatePatientInput): Promise<void>;
  // [US-PM-07] Re-send the app-install SMS for an already-registered patient (Root-only). Unlike the
  // create call, this one DOES report a gateway failure — 409 PATIENT_SMS_SEND_FAILED.
  resendInstallSms(patientId: number): Promise<void>;
}
